/**
 * Hand-positioned SVG diagram for the data-flow page. Replaces the ASCII
 * art that the page used to render in a fenced ``` block. Same goals as
 * ContractsArchitecture: pixel-perfect layout, theme-aware via CSS
 * variables, no layout-engine fights.
 *
 * What it shows:
 *   - Sepolia at the top hosting the six v3 contracts
 *   - Three frontends below sharing one ethers JSON-RPC channel up to chain
 *   - The actors driving each frontend: users for metha-client and
 *     metha-docs, an injected wallet (MetaMask) for metha-admin-v2
 *
 * Phase 1 + 2 snapshot — no central API tier, no shared database.
 */
export default function DataFlowDiagram() {
  return (
    <div className="data-flow-wrap" data-pagefind-ignore>
      <svg
        viewBox="0 0 900 540"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Current data flow: three frontends (metha-client at metha.life, metha-admin-v2 at sepolia.metha.life, metha-docs at docs.metha.life) connect via ethers JSON-RPC to Sepolia, which hosts the six v3 contracts. Users drive metha-client and metha-docs; an injected wallet (MetaMask) drives metha-admin-v2."
        className="data-flow-svg"
      >
        <defs>
          <marker id="df-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 z" className="ca-arrow-head" />
          </marker>
        </defs>

        {/* ── Top: Sepolia (the chain) with inner "6 contracts" box ─── */}
        <g className="ca-node">
          <rect x="300" y="20" width="300" height="150" rx="8" />
          <text x="450" y="48" textAnchor="middle" className="ca-title">Sepolia (the chain)</text>

          {/* Inner contracts box — visually contained within Sepolia. */}
          <rect x="350" y="80" width="200" height="75" rx="6" className="df-inner-rect" />
          <text x="450" y="108" textAnchor="middle" className="ca-title" style={{ fontSize: '13px' }}>6 contracts (v3)</text>
          <text x="450" y="128" textAnchor="middle" className="ca-subtitle">EternalStorage · MethaToken · Crowdsale</text>
          <text x="450" y="145" textAnchor="middle" className="ca-subtitle">OpenGovernance · AuditingPool · AR</text>
        </g>

        {/* ── The shared JSON-RPC bus: horizontal bar at y=230 with three
             verticals down to the frontends and one upward arrow to chain. */}
        <line x1="170" y1="230" x2="730" y2="230" className="ca-edge" />
        <line x1="450" y1="230" x2="450" y2="172" className="ca-edge" markerEnd="url(#df-arrow)" />
        <text x="458" y="200" className="ca-label">ethers JSON-RPC</text>

        {/* Verticals from each frontend top up to the bus */}
        <line x1="170" y1="290" x2="170" y2="230" className="ca-edge" />
        <line x1="450" y1="290" x2="450" y2="230" className="ca-edge" />
        <line x1="730" y1="290" x2="730" y2="230" className="ca-edge" />

        {/* ── Three frontend boxes (220 wide × 120 tall) ───────── */}
        <g className="ca-node">
          {/* metha-client */}
          <rect x="60" y="290" width="220" height="120" rx="8" />
          <text x="170" y="320" textAnchor="middle" className="ca-title">metha-client</text>
          <text x="170" y="343" textAnchor="middle" className="ca-subtitle">landing site</text>
          <text x="170" y="362" textAnchor="middle" className="ca-subtitle">reads only</text>
          <text x="170" y="392" textAnchor="middle" className="df-url">metha.life</text>

          {/* metha-admin-v2 */}
          <rect x="340" y="290" width="220" height="120" rx="8" />
          <text x="450" y="320" textAnchor="middle" className="ca-title">metha-admin-v2</text>
          <text x="450" y="343" textAnchor="middle" className="ca-subtitle">the dapp</text>
          <text x="450" y="362" textAnchor="middle" className="ca-subtitle">read + write</text>
          <text x="450" y="392" textAnchor="middle" className="df-url">sepolia.metha.life</text>

          {/* metha-docs */}
          <rect x="620" y="290" width="220" height="120" rx="8" />
          <text x="730" y="320" textAnchor="middle" className="ca-title">metha-docs</text>
          <text x="730" y="343" textAnchor="middle" className="ca-subtitle">this site</text>
          <text x="730" y="362" textAnchor="middle" className="ca-subtitle">reads via widgets</text>
          <text x="730" y="392" textAnchor="middle" className="df-url">docs.metha.life</text>
        </g>

        {/* ── Bottom: actors driving each frontend (arrows go UP from
             actor → frontend since actors initiate the interaction). */}
        <line x1="170" y1="460" x2="170" y2="412" className="ca-edge" markerEnd="url(#df-arrow)" />
        <line x1="450" y1="460" x2="450" y2="412" className="ca-edge" markerEnd="url(#df-arrow)" />
        <line x1="730" y1="460" x2="730" y2="412" className="ca-edge" markerEnd="url(#df-arrow)" />

        <g className="ca-node ca-node-actor">
          <rect x="110" y="465" width="120" height="40" rx="20" />
          <text x="170" y="490" textAnchor="middle" className="ca-actor">users</text>

          <rect x="340" y="465" width="220" height="40" rx="20" />
          <text x="450" y="490" textAnchor="middle" className="ca-actor">MetaMask / injected wallet</text>

          <rect x="670" y="465" width="120" height="40" rx="20" />
          <text x="730" y="490" textAnchor="middle" className="ca-actor">users</text>
        </g>
      </svg>
    </div>
  );
}
